import { usePrefetchedSuspenseQuery } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";
import { useMorphemeMnemonicsPanelMorphemeUserSliceQueryOptions } from "@-/dev.improve.haniverse.lib.morpheme.read";
import { useResolvedQuery } from "@-/dev.improve.haniverse.lib.query-options";
import { MorphemePanelsFlashcardProviders } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels";
import { NewMorphemeLessonLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.new-morpheme._context";
import { type ReactNode, useMemo } from "react";

/**
 * The panels providers a lesson card's back and Info need. The mnemonics navigation props come
 * from the lesson layout context, so the deck's "Next Pronunciation" control steps the same tabs
 * the old lesson footer did, and its binder tab colours the readings this lesson introduces.
 */
export function LessonFlashcardProvidersSuspense({ children }: { children: ReactNode }) {
  const [
    activeIndex,
    grapheme,
    introducedPinyinList,
    lessonPart,
    mnemonicsSlice,
    setActiveIndex,
    viewNavigationRef,
  ] = useContextSelectors(
    NewMorphemeLessonLayoutContext,
    (c) => c.activeIndex,
    (c) => c.grapheme,
    (c) => c.introducedPinyinList,
    (c) => c.lessonPart,
    (c) => c.morphemeMnemonicsPanelMorphemeSlice,
    (c) => c.setActiveIndex,
    (c) => c.viewNavigationRef,
  );
  const mnemonicsUserSliceQueryOptions =
    useMorphemeMnemonicsPanelMorphemeUserSliceQueryOptions(grapheme);
  const { data: mnemonicsUserSlice } = usePrefetchedSuspenseQuery(mnemonicsUserSliceQueryOptions);
  const resolvedMnemonicsUserSlice = useResolvedQuery(
    mnemonicsUserSliceQueryOptions.queryKey,
    mnemonicsUserSlice,
  );
  const morphemeHrefObject = useMemo<MorphemeHrefObject>(
    () => ({
      href: "/(nav)/(app)/learn/explore/morpheme/[morpheme]",
      params: { lesson: grapheme, morpheme: grapheme },
    }),
    [grapheme],
  );
  // The lesson decides which subject is being taught. Primitive and glyph-only introductions
  // show their meaning cue without pronunciation tabs, even if the dictionary knows readings.
  const lessonMnemonicsSlice =
    lessonPart === "primitive" || lessonPart === "meaning"
      ? { ...mnemonicsSlice, data: { ...mnemonicsSlice.data, pinyinList: [] } }
      : mnemonicsSlice;

  return (
    <MorphemePanelsFlashcardProviders
      activeIndex={activeIndex}
      character={grapheme}
      introducedPinyinList={introducedPinyinList}
      mnemonicsSlice={lessonMnemonicsSlice}
      mnemonicsUserSlice={resolvedMnemonicsUserSlice}
      morphemeHrefObject={morphemeHrefObject}
      setActiveIndex={setActiveIndex}
      viewNavigationRef={viewNavigationRef}
    >
      {children}
    </MorphemePanelsFlashcardProviders>
  );
}
